import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Inject,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { DOCUMENT } from '@angular/common';
import { Store } from '@ngrx/store';
import * as R from 'ramda';
import { config } from '../config/country-config';
import {
  basicInfoData,
  FieldsConfig,
  ErrorMsgs,
  FieldConfig,
  FormControlTypes,
  FieldNames,
} from '../registration/registration.model';
import {
  DOBValidator,
  calculateAge,
} from '../validators/date-of-birth.validators';
import { Labels, RegistrationLabels, SdfCardLabels } from '../app.models';
import { isSdfCardFlow } from '../config/app-config';
import { SenderRegistrationInfo } from '../store/transaction/types';
import { MatLegacyAutocompleteTrigger as MatAutocompleteTrigger } from '@angular/material/legacy-autocomplete';
import { FormSearchUtils } from '../shared/utils/form-search-utils';
import { selectFeeQuoteState } from '../store/fee-quote/fee-quote.selectors';

@Component({
  selector: 'app-basic-info',
  templateUrl: './basic-info.component.html',
  styleUrls: ['./basic-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BasicInfoComponent implements OnInit {
  phoneCountryCode: string = '';
  registrationLabels!: RegistrationLabels;
  dateOfBirthComponent!: any;
  isNewUser: boolean = false;
  reviewStatus: string;
  initialCountrySelected: boolean = false;
  formSearchUtils = new FormSearchUtils();
  public _google: any;

  @Input() isStateFieldError: any;
  @Input() locale: string;
  @Input() isCountryFieldError: any;

  private _labels: any;
  @Input() public set labels(labels: Labels) {
    this._labels = labels;
    this.sdfCardLabels = labels.stellar.sdfcard;
    this.registrationLabels = this.labels.stellar.registration;
    this.dateOfBirthComponent =
      this.labels.stellar.registration?.dateOfBirthComponent;
    if (this.labels.stellar.registration) {
      const personalFields =
        basicInfoData &&
        basicInfoData?.fields &&
        basicInfoData?.fields?.personalInfoFields &&
        basicInfoData?.fields?.personalInfoFields?.filter((field) => {
          field.fieldLabel = labels.stellar.registration[field.name];
          return field;
        });
      basicInfoData.fields.personalInfoFields = personalFields;
    }
  }
  public get labels() {
    return this._labels;
  }

  private _transactionCountryCode?: string;
  @Input() public set transactionCountryCode(code: string) {
    this._transactionCountryCode = code;
  }

  public get transactionCountryCode() {
    return this._transactionCountryCode!;
  }

  private _senderData?: SenderRegistrationInfo;
  @Input() public set senderData(data: SenderRegistrationInfo) {
    this._senderData = data;
  }

  public get senderData(): SenderRegistrationInfo {
    return this._senderData!;
  }

  private _countries?: any;
  @Input() public set countries(countries: any) {
    this._countries = countries;
  }

  public get countries(): any {
    return this._countries;
  }

  public form!: FormGroup;
  public dateOfBirth!: FormGroup;
  public basicInfoData = basicInfoData;
  public fields: FieldsConfig = {};
  private fieldsTemplate: any;
  public personalFields!: FieldConfig[];
  public errorMsgToDisplay: string = 'field has error';
  public ErrorMsgs = ErrorMsgs;
  public dateFrom: Date = new Date();
  public readonly controlTypes: typeof FormControlTypes = FormControlTypes;
  public readonly countryConfig = config;
  public readonly formFields: typeof FieldNames = FieldNames;
  public mobileNumberConfig!: FieldConfig;
  public mobileCountryConfig!: FieldConfig;
  public defaultCountryCode!: string;
  public phoneData!: any;
  public autocomplete: any;
  public stateOptions: any = [];
  public country!: any;
  public isSdfCardFlow: boolean = false;
  sdfCardLabels: SdfCardLabels;

  isDOBValid: boolean = false;
  isDOBError: boolean = false;
  isConfirmed: boolean = false;
  personalDetailsAreValid: boolean = false;
  @ViewChildren(MatAutocompleteTrigger)
  triggers: QueryList<MatAutocompleteTrigger>;

  @Output() public submitted: EventEmitter<any> = new EventEmitter<any>();
  constructor(
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private store: Store<any>,
    @Inject(DOCUMENT) private document: any
  ) {
    this.isSdfCardFlow = isSdfCardFlow;
  }

  ngOnInit(): void {
    this.form = this.fb.group({});

    this.store.select(selectFeeQuoteState).subscribe((feeQuote) => {
      const data = feeQuote.senderProfile;
      const isExistingUser =
        !R.isEmpty(data) &&
        data?.firstName &&
        data?.lastName &&
        !R.isEmpty(data.address) &&
        data.address?.line1
          ? true
          : false;
      this.isNewUser = !isExistingUser;
      if (data) {
        this.senderData = data;
      }
      if (isExistingUser) {
        this.setDefaultValues();
      }
    });

    document.addEventListener('onautocomplete', function (e) {
      e.preventDefault(); // prevent autocomplete
    });
    this.fieldsTemplate = basicInfoData.personalFieldsTemplate;
    this.personalFields = basicInfoData.fields?.personalInfoFields!;

    this.addFieldValidators(basicInfoData.fields!);
  }

  private addFieldValidators(fields: FieldsConfig) {
    const allFields = fields.personalInfoFields!;
    this.fieldsTemplate.forEach((fieldName: any) => {
      const fieldConfig = allFields?.find(
        (fieldConfig: FieldConfig) => fieldConfig.name === fieldName
      );

      if (
        fieldName === this.formFields.mobileNumber ||
        fieldName === this.formFields.mobileCountry
      ) {
        if (fieldName === FieldNames.mobileNumber && fieldConfig) {
          this.mobileNumberConfig = fieldConfig;
        }
        if (fieldName === FieldNames.mobileCountry && fieldConfig) {
          this.mobileCountryConfig = fieldConfig;
        }
      } else {
        const validators: ValidatorFn[] = [];
        if (fieldConfig?.required) {
          validators.push(Validators.required);
        }

        if (fieldConfig?.pattern && fieldName !== FieldNames.dob) {
          validators.push(Validators.pattern(fieldConfig!.pattern));
        }

        if (fieldConfig?.pattern && fieldName === FieldNames.dob) {
          validators.push(DOBValidator());
        }

        if (fieldConfig?.fieldMaxLength) {
          validators.push(Validators.maxLength(fieldConfig!.fieldMaxLength));
        }

        if (fieldConfig?.fieldMinLength) {
          validators.push(Validators.minLength(fieldConfig!.fieldMinLength));
        }

        this.form.addControl(fieldName, this.fb.control('', { validators }));
      }

      if (fieldConfig?.disabled) {
        this.form.get(fieldName)?.disable();
      }
    });
  }

  public getErrorsMsg(fieldName: any, fieldConfig: any): any {
    if (fieldConfig.invalid && (fieldConfig.touched || fieldConfig.dirty)) {
      if (fieldConfig.hasError('required')) {
        return (this.errorMsgToDisplay =
          fieldName + ' ' + this.labels?.stellar?.error?.isRequired);
      } else if (fieldConfig.hasError('max')) {
        return (this.errorMsgToDisplay =
          fieldName +
          ' ' +
          this.labels?.stellar?.registration?.errorMsgs?.maxLength);
      } else if (fieldConfig.hasError('min')) {
        return (this.errorMsgToDisplay =
          fieldName +
          ' ' +
          this.labels?.stellar?.registration?.errorMsgs?.minLength);
      } else {
        return (this.errorMsgToDisplay =
          fieldName +
          ' ' +
          this.labels?.stellar?.registration?.errorMsgs?.invalid);
      }
    }
  }

  private setDefaultValues() {
    !!this.personalFields &&
      this.personalFields.forEach((field: any) => {
        if (
          this.senderData[field.name] &&
          field.name !== 'dateOfBirth' &&
          this.senderData[field.name].match(field.pattern)
        ) {
          const data = field.fieldMaxLength
            ? this.senderData[field.name].substring(0, field.fieldMaxLength)
            : this.senderData[field.name];
          this.form.controls[field.name].setValue(data);
          this.form.controls[field.name].disable();
        } else if (
          this.senderData[field.name] &&
          field.name === 'dateOfBirth'
        ) {
          const age = calculateAge(this.senderData.dateOfBirth || '');
          this.form.controls[field.name].setValue(this.senderData[field.name]);
          this.isDOBValid = age >= 18 && age < 100;
        }
      });

    if (this._senderData?.mobilePhone) {
      this.phoneData = {
        ...this._senderData.mobilePhone,
        country: this.country,
      };
    }

    this.cdr.detectChanges();
  }
}
